/**
 * Volumetric dressing for the street rig — the parts of the light you can
 * SEE in the rain:
 *   - additive fresnel cones hanging under each streetlamp head
 *   - an additive haze quad floating in front of the neon sign
 *   - fake wet-asphalt reflection smears (additive gradient quads on the
 *     road) under each lamp and spilling from the neon across the street
 * All procedural: shader cones + canvas gradient textures. No asset files.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Scene,
  ShaderMaterial
} from 'three'
import type { Vec3 } from '../core/types'
import { lerp } from './blend'
import {
  CONE_BOTTOM_RADIUS,
  CONE_FRESNEL_POWER,
  CONE_OPACITY,
  CONE_RADIAL_SEGMENTS,
  CONE_TOP_RADIUS,
  GLOW_HEIGHT,
  GLOW_LAMP_LENGTH,
  GLOW_LAMP_OPACITY,
  GLOW_LAMP_WIDTH,
  GLOW_NEON_LENGTH,
  GLOW_NEON_OPACITY,
  GLOW_NEON_WIDTH,
  GLOW_TEXTURE_SIZE_PX,
  LAMP_COLOR,
  LAMP_HEIGHT,
  NEON_COLOR,
  NEON_HAZE_OPACITY,
  NEON_HAZE_SIZE,
  NEON_POSITION,
  RAIN_INDOOR_SCALE
} from './constants'

export interface VolumetricsRig {
  /** weight: 1 outdoor, 0 indoor. dim: finale multiplier. */
  applyBlend(weight: number, dim: number): void
}

const CONE_VERTEX_SHADER = /* glsl */ `
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vHeight;

  void main() {
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    // CylinderGeometry y runs -h/2 (bottom) .. +h/2 (top); 1 at lamp head.
    vHeight = uv.y;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`

const CONE_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vHeight;

  void main() {
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    // Bright when looking through the cone's core, fading at the silhouette.
    float facing = abs(dot(viewDir, normalize(vWorldNormal)));
    float core = pow(facing, ${CONE_FRESNEL_POWER.toFixed(2)});
    // Strongest at the lamp head, dissolving toward the street.
    float fade = smoothstep(0.0, 0.85, vHeight);
    gl_FragColor = vec4(uColor, uOpacity * core * fade);
  }
`

/** Radial gradient blob, white center fading to transparent black. */
const createGlowTexture = (): CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = GLOW_TEXTURE_SIZE_PX
  canvas.height = GLOW_TEXTURE_SIZE_PX
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('GUMSHOE lighting: 2D canvas context unavailable for glow')
  }
  const half = GLOW_TEXTURE_SIZE_PX / 2
  const gradient = context.createRadialGradient(half, half, 0, half, half, half)
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
  gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.35)')
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
  context.fillStyle = gradient
  context.fillRect(0, 0, GLOW_TEXTURE_SIZE_PX, GLOW_TEXTURE_SIZE_PX)
  return new CanvasTexture(canvas)
}

interface FadeEntry {
  readonly material: ShaderMaterial | MeshBasicMaterial
  readonly baseOpacity: number
}

const createCone = (lamp: Vec3, entries: FadeEntry[]): Mesh => {
  const geometry = new CylinderGeometry(
    CONE_TOP_RADIUS,
    CONE_BOTTOM_RADIUS,
    LAMP_HEIGHT,
    CONE_RADIAL_SEGMENTS,
    1,
    true
  )
  const material = new ShaderMaterial({
    vertexShader: CONE_VERTEX_SHADER,
    fragmentShader: CONE_FRAGMENT_SHADER,
    uniforms: {
      uColor: { value: new Color(LAMP_COLOR) },
      uOpacity: { value: CONE_OPACITY }
    },
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide
  })
  entries.push({ material, baseOpacity: CONE_OPACITY })
  const cone = new Mesh(geometry, material)
  cone.position.set(lamp.x, LAMP_HEIGHT / 2, lamp.z)
  return cone
}

const createGroundGlow = (
  texture: CanvasTexture,
  color: number,
  opacity: number,
  width: number,
  length: number,
  entries: FadeEntry[]
): Mesh => {
  const material = new MeshBasicMaterial({
    map: texture,
    color,
    transparent: true,
    opacity,
    blending: AdditiveBlending,
    depthWrite: false
  })
  entries.push({ material, baseOpacity: opacity })
  const quad = new Mesh(new PlaneGeometry(width, length), material)
  quad.rotation.x = -Math.PI / 2
  return quad
}

const createNeonHaze = (texture: CanvasTexture, entries: FadeEntry[]): Mesh => {
  const material = new MeshBasicMaterial({
    map: texture,
    color: NEON_COLOR,
    transparent: true,
    opacity: NEON_HAZE_OPACITY,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide
  })
  entries.push({ material, baseOpacity: NEON_HAZE_OPACITY })
  const quad = new Mesh(new PlaneGeometry(NEON_HAZE_SIZE, NEON_HAZE_SIZE * 0.55), material)
  // The sign faces -X; hang the haze a meter off the wall, parallel to it.
  quad.rotation.y = -Math.PI / 2
  quad.position.set(NEON_POSITION.x - 1.0, NEON_POSITION.y, NEON_POSITION.z)
  return quad
}

export const createVolumetricsRig = (
  scene: Scene,
  lampPositions: readonly Vec3[]
): VolumetricsRig => {
  const group = new Group()
  group.name = 'light-volumetrics'
  const entries: FadeEntry[] = []
  const glowTexture = createGlowTexture()

  lampPositions.forEach((lamp) => {
    group.add(createCone(lamp, entries))
    const glow = createGroundGlow(
      glowTexture,
      LAMP_COLOR,
      GLOW_LAMP_OPACITY,
      GLOW_LAMP_WIDTH,
      GLOW_LAMP_LENGTH,
      entries
    )
    glow.position.set(lamp.x, GLOW_HEIGHT, lamp.z)
    group.add(glow)
  })

  // Neon spill: smear stretched across the road (along X, toward camera-side).
  const neonGlow = createGroundGlow(
    glowTexture,
    NEON_COLOR,
    GLOW_NEON_OPACITY,
    GLOW_NEON_WIDTH,
    GLOW_NEON_LENGTH,
    entries
  )
  neonGlow.rotation.z = Math.PI / 2
  neonGlow.position.set(NEON_POSITION.x - 3.4, GLOW_HEIGHT, NEON_POSITION.z)
  group.add(neonGlow)

  group.add(createNeonHaze(glowTexture, entries))
  scene.add(group)

  const applyBlend = (weight: number, dim: number): void => {
    const scale = lerp(RAIN_INDOOR_SCALE, 1, weight) * dim
    entries.forEach(({ material, baseOpacity }) => {
      if (material instanceof ShaderMaterial) {
        const uniform = material.uniforms['uOpacity']
        if (uniform) uniform.value = baseOpacity * scale
      } else {
        material.opacity = baseOpacity * scale
      }
    })
  }

  return { applyBlend }
}
